import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  PatchAdminV1StoresStoreIdStockLocationsIdBody,
  PostAdminV1StoresStoreIdStockLocationsBody,
} from '@nucleus-commerce/api-client/admin';

import { inventoryApi } from '../api/inventory-api';

import { inventoryKeys } from './inventory-keys';

/** A new default changes which location an unfiltered levels list shows, so levels refetch too. */
function useInvalidateLocations(storeId: string) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: inventoryKeys.all(storeId) });
}

export function useCreateLocation(storeId: string) {
  const invalidate = useInvalidateLocations(storeId);
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdStockLocationsBody) =>
      inventoryApi.createLocation(storeId, body),
    meta: { successMessage: 'inventory:toasts.locationCreated', silentError: true },
    onSuccess: invalidate,
  });
}

export function useUpdateLocation(storeId: string) {
  const invalidate = useInvalidateLocations(storeId);
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: PatchAdminV1StoresStoreIdStockLocationsIdBody;
    }) => inventoryApi.updateLocation(storeId, id, body),
    meta: { successMessage: 'inventory:toasts.locationUpdated', silentError: true },
    onSuccess: invalidate,
  });
}

/** 409 for the default location or one that still holds stock; the dialog explains which. */
export function useDeleteLocation(storeId: string) {
  const invalidate = useInvalidateLocations(storeId);
  return useMutation({
    mutationFn: (id: string) => inventoryApi.removeLocation(storeId, id),
    meta: { successMessage: 'inventory:toasts.locationDeleted', silentError: true },
    onSuccess: invalidate,
  });
}
